import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const StudentPerformanceTable = ({ students }) => {
  const navigate = useNavigate();
  const [sortField, setSortField] = useState('xp');
  const [sortDirection, setSortDirection] = useState('desc');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortedStudents = [...students]?.sort((a, b) => {
    const aValue = a?.[sortField];
    const bValue = b?.[sortField];
    const modifier = sortDirection === 'asc' ? 1 : -1;
    return aValue > bValue ? modifier : -modifier;
  });

  const getSortIcon = (field) => {
    if (sortField !== field) return 'ChevronsUpDown';
    return sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown';
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted border-b border-border">
            <tr>
              <th className="text-left px-6 py-4 text-sm font-semibold text-card-foreground">Student</th>
              <th 
                className="text-left px-6 py-4 text-sm font-semibold text-card-foreground cursor-pointer hover:bg-muted-foreground/10 transition-colors"
                onClick={() => handleSort('xp')}
              >
                <div className="flex items-center gap-2">
                  XP Points
                  <Icon name={getSortIcon('xp')} size={16} />
                </div>
              </th>
              <th 
                className="text-left px-6 py-4 text-sm font-semibold text-card-foreground cursor-pointer hover:bg-muted-foreground/10 transition-colors"
                onClick={() => handleSort('quizzesCompleted')}
              >
                <div className="flex items-center gap-2">
                  Quizzes
                  <Icon name={getSortIcon('quizzesCompleted')} size={16} />
                </div>
              </th>
              <th 
                className="text-left px-6 py-4 text-sm font-semibold text-card-foreground cursor-pointer hover:bg-muted-foreground/10 transition-colors"
                onClick={() => handleSort('badges')}
              >
                <div className="flex items-center gap-2">
                  Badges
                  <Icon name={getSortIcon('badges')} size={16} />
                </div>
              </th>
              <th className="text-left px-6 py-4 text-sm font-semibold text-card-foreground">Last Active</th>
              <th className="text-right px-6 py-4 text-sm font-semibold text-card-foreground">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sortedStudents?.map((student) => (
              <tr key={student?.id} className="hover:bg-muted transition-colors">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    {student?.avatar ? (
                      <Image 
                        src={student?.avatar} 
                        alt={student?.avatarAlt}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold">
                        {student?.name?.charAt(0)?.toUpperCase()}
                      </div>
                    )}
                    <div>
                      <p className="text-sm font-medium text-card-foreground">{student?.name}</p>
                      <p className="text-xs text-muted-foreground">{student?.email}</p>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <Icon name="Zap" size={16} className="text-accent" />
                    <span className="text-sm font-semibold text-card-foreground">{student?.xp?.toLocaleString()}</span>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm text-card-foreground">{student?.quizzesCompleted}</span>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <Icon name="Award" size={16} className="text-secondary" />
                    <span className="text-sm text-card-foreground">{student?.badges}</span>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm text-muted-foreground">{student?.lastActive}</span>
                </td>
                <td className="px-6 py-4 text-right">
                  <Button 
                    variant="ghost" 
                    size="sm"
                    iconName="Eye"
                    onClick={() => navigate(`/user-management?user=${student?.id}`)}
                  >
                    View
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default StudentPerformanceTable;